/**
 * Notification SSE Service
 *
 * Pure SSE connection logic -- no React dependencies.
 * Handles EventSource lifecycle, reconnection with exponential backoff,
 * and heartbeat staleness detection.
 *
 * Follows the same architecture as entities/user/services/verificationSSE.ts.
 *
 * SSE endpoint: GET /api/v1/notifications/stream?token=JWT
 * Events: connected, notification, read, read_all, heartbeat
 *
 * @module entities/notification/services/notificationSSE
 */

import { buildApiUrl } from '@lib/api/config';
import type {
  NotificationSSECallbacks,
  NotificationSSEConnection,
  NotificationSSEConnectionState,
  NotificationSSEConnectedEvent,
  NotificationSSENewEvent,
  NotificationSSEReadEvent,
  NotificationSSEReadAllEvent,
  NotificationSSEHeartbeatEvent,
} from '../types/notificationSSE.types';

// ─────────────────────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────────────────────

const SSE_CONFIG = {
  /** SSE endpoint path */
  endpoint: '/notifications/stream',
  /** Max reconnection attempts before giving up */
  maxRetries: 5,
  /** Base delay for exponential backoff (ms) */
  baseRetryDelay: 1000,
  /** Maximum retry delay cap (ms) */
  maxRetryDelay: 10000,
  /** Timeout to receive 'connected' event (ms) */
  connectionTimeout: 30000,
  /** Heartbeat interval from server (ms) */
  heartbeatInterval: 15000,
  /** How many missed heartbeats before reconnecting */
  missedHeartbeatsThreshold: 3,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Service Implementation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Create a notification SSE connection
 *
 * Connects to the server's SSE stream and dispatches events via callbacks.
 * Handles reconnection with exponential backoff and heartbeat monitoring.
 *
 * @param token - JWT access token for authentication
 * @param callbacks - Event callbacks
 * @returns Connection control object (close, getState, isActive)
 */
export function createNotificationSSEConnection(
  token: string,
  callbacks: NotificationSSECallbacks
): NotificationSSEConnection {
  let eventSource: EventSource | null = null;
  let state: NotificationSSEConnectionState = 'disconnected';
  let retryCount = 0;
  let retryTimeout: ReturnType<typeof setTimeout> | null = null;
  let connectionTimeout: ReturnType<typeof setTimeout> | null = null;
  let heartbeatTimeout: ReturnType<typeof setTimeout> | null = null;
  let isClosedIntentionally = false;

  // ─────────────────────────────────────────────────────────────────────────
  // State Management
  // ─────────────────────────────────────────────────────────────────────────

  function setState(newState: NotificationSSEConnectionState): void {
    if (state !== newState) {
      state = newState;
      callbacks.onStateChange?.(newState);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Cleanup
  // ─────────────────────────────────────────────────────────────────────────

  function cleanup(): void {
    if (retryTimeout) {
      clearTimeout(retryTimeout);
      retryTimeout = null;
    }
    if (connectionTimeout) {
      clearTimeout(connectionTimeout);
      connectionTimeout = null;
    }
    if (heartbeatTimeout) {
      clearTimeout(heartbeatTimeout);
      heartbeatTimeout = null;
    }
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Heartbeat Monitoring
  // ─────────────────────────────────────────────────────────────────────────

  function resetHeartbeatTimer(): void {
    if (heartbeatTimeout) {
      clearTimeout(heartbeatTimeout);
    }

    // If no heartbeat received within threshold, reconnect
    const stalenessDelay =
      SSE_CONFIG.heartbeatInterval * SSE_CONFIG.missedHeartbeatsThreshold;

    heartbeatTimeout = setTimeout(() => {
      if (isClosedIntentionally || state !== 'connected') return;
      console.warn('Notification SSE: Heartbeat timeout, reconnecting...');
      cleanup();
      scheduleRetry();
    }, stalenessDelay);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Reconnection with Exponential Backoff
  // ─────────────────────────────────────────────────────────────────────────

  function scheduleRetry(): void {
    if (isClosedIntentionally) return;

    if (retryCount >= SSE_CONFIG.maxRetries) {
      setState('error');
      callbacks.onError?.(
        new Error(
          `Failed to connect after ${SSE_CONFIG.maxRetries} attempts`
        ),
        'MAX_RETRIES_EXCEEDED'
      );
      return;
    }

    retryCount++;
    const delay = Math.min(
      SSE_CONFIG.baseRetryDelay * Math.pow(2, retryCount - 1),
      SSE_CONFIG.maxRetryDelay
    );

    retryTimeout = setTimeout(connect, delay);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Connection
  // ─────────────────────────────────────────────────────────────────────────

  function connect(): void {
    cleanup();
    setState('connecting');

    // Fetch fresh token from localStorage on each reconnect attempt
    // (auth context's proactive refresh may have updated it)
    const freshToken =
      localStorage.getItem('amalq_user_access_token') || token;

    const baseUrl = buildApiUrl(SSE_CONFIG.endpoint);
    const url = `${baseUrl}?token=${encodeURIComponent(freshToken)}`;

    try {
      eventSource = new EventSource(url);

      // Connection timeout -- retry if no 'connected' event in time
      connectionTimeout = setTimeout(() => {
        if (state === 'connecting') {
          cleanup();
          scheduleRetry();
        }
      }, SSE_CONFIG.connectionTimeout);

      // ─── Event Handlers ────────────────────────────────────────────

      eventSource.addEventListener('connected', (e: MessageEvent) => {
        if (connectionTimeout) {
          clearTimeout(connectionTimeout);
          connectionTimeout = null;
        }
        retryCount = 0;
        setState('connected');
        resetHeartbeatTimer();

        try {
          const data: NotificationSSEConnectedEvent = JSON.parse(e.data);
          callbacks.onConnected?.(data);
        } catch (err) {
          console.error('Notification SSE: Failed to parse connected event', err);
        }
      });

      eventSource.addEventListener('notification', (e: MessageEvent) => {
        resetHeartbeatTimer();
        try {
          const data: NotificationSSENewEvent = JSON.parse(e.data);
          callbacks.onNotification?.(data);
        } catch (err) {
          console.error('Notification SSE: Failed to parse notification event', err);
        }
      });

      eventSource.addEventListener('read', (e: MessageEvent) => {
        resetHeartbeatTimer();
        try {
          const data: NotificationSSEReadEvent = JSON.parse(e.data);
          callbacks.onRead?.(data);
        } catch (err) {
          console.error('Notification SSE: Failed to parse read event', err);
        }
      });

      eventSource.addEventListener('read_all', (e: MessageEvent) => {
        resetHeartbeatTimer();
        try {
          const data: NotificationSSEReadAllEvent = JSON.parse(e.data);
          callbacks.onReadAll?.(data);
        } catch (err) {
          console.error('Notification SSE: Failed to parse read_all event', err);
        }
      });

      eventSource.addEventListener('heartbeat', (e: MessageEvent) => {
        resetHeartbeatTimer();
        try {
          const data: NotificationSSEHeartbeatEvent = JSON.parse(e.data);
          callbacks.onHeartbeat?.(data);
        } catch {
          // Heartbeat parse errors are non-critical
        }
      });

      // Native EventSource error (connection drop, server close)
      // Force close to prevent native auto-reconnect from fighting custom backoff
      eventSource.onerror = () => {
        if (isClosedIntentionally) return;

        eventSource?.close();
        setState('disconnected');
        scheduleRetry();
      };
    } catch (err) {
      setState('error');
      callbacks.onError?.(
        err instanceof Error
          ? err
          : new Error('Failed to create SSE connection')
      );
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public Interface
  // ─────────────────────────────────────────────────────────────────────────

  // Start connection immediately
  connect();

  return {
    close: () => {
      isClosedIntentionally = true;
      cleanup();
      setState('disconnected');
    },
    getState: () => state,
    isActive: () => state === 'connected' || state === 'connecting',
  };
}
